import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { requestWhatsAppLinkCode } from '../../services/whatsappService';

/**
 * @param {'section' | 'panel'} [variant='section']
 *   section — full card for Minha Conta
 *   panel — compact body for Home collapsible
 */
const ConnectWhatsApp = ({ variant = 'section' }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');
  const [expiresIn, setExpiresIn] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setCopied(false);

    try {
      const result = await requestWhatsAppLinkCode();
      if (!result.ok) {
        setError(result.message || 'Não foi possível gerar o código.');
        setCode('');
        return;
      }
      setCode(result.code);
      setBusinessNumber(result.businessDisplayNumber || '');
      setExpiresIn(result.expiresIn ?? null);
    } catch (err) {
      console.error('ConnectWhatsApp generate failed:', err);
      setError('Não foi possível gerar o código.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      setCopied(false);
    }
  };

  const whatsappChatUrl = (() => {
    if (!code || !businessNumber) return null;
    const digits = String(businessNumber).replace(/\D/g, '');
    if (!digits) return null;
    return `https://wa.me/${digits}?text=${encodeURIComponent(code)}`;
  })();

  const expiresLabel =
    typeof expiresIn === 'number' && expiresIn > 0
      ? `Válido por cerca de ${Math.round(expiresIn / 60)} minutos.`
      : null;

  const body = (
    <div className="space-y-4">
      <Button
        variant="outline"
        size="sm"
        iconName="Link"
        iconPosition="left"
        loading={loading}
        onClick={handleGenerate}
      >
        Gerar código de vinculação
      </Button>

      {error && (
        <div className="p-4 bg-error/10 border border-error/20 rounded-md">
          <p className="text-sm text-error font-body">{error}</p>
        </div>
      )}

      {code && (
        <div className="space-y-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
          <p className="text-sm text-muted-foreground font-body">
            Envie este código para o nosso WhatsApp
            {businessNumber ? (
              <>
                :{' '}
                {whatsappChatUrl ? (
                  <a
                    href={whatsappChatUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-primary underline underline-offset-2 hover:text-primary/80"
                  >
                    {businessNumber}
                  </a>
                ) : (
                  <span className="font-medium text-foreground">{businessNumber}</span>
                )}
              </>
            ) : (
              '.'
            )}
          </p>
          <p className="text-3xl md:text-4xl font-mono tracking-widest text-foreground text-center py-2">
            {code}
          </p>
          {expiresLabel && (
            <p className="text-xs text-muted-foreground text-center">{expiresLabel}</p>
          )}
          <Button fullWidth variant="outline" iconName="Copy" onClick={handleCopy}>
            {copied ? 'Código copiado' : 'Copiar código'}
          </Button>
        </div>
      )}
    </div>
  );

  if (variant === 'panel') {
    return (
      <div
        className="rounded-lg border border-border bg-muted/20 p-4 md:p-5"
        data-tour="tour-connect-whatsapp-panel"
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="flex items-center justify-center w-9 h-9 bg-primary/10 rounded-lg shrink-0">
            <Icon name="MessageCircle" size={18} color="var(--color-primary)" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-foreground">
              Vincular WhatsApp
            </h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Gere um código e envie no WhatsApp do neo-modelos para vincular seu
              número ao assistente.
            </p>
          </div>
        </div>
        {body}
      </div>
    );
  }

  return (
    <section className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8 transition-smooth">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-primary/10 rounded-lg">
          <Icon name="MessageCircle" size={20} color="var(--color-primary)" />
        </div>
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Conectar WhatsApp
          </h2>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Vincule seu número para usar o assistente pelo WhatsApp.
          </p>
        </div>
      </div>

      {body}
    </section>
  );
};

export default ConnectWhatsApp;
